// Client-side email helpers — spec §6.
//
// THREE transactional emails:
//   1. booked     → fired right after the customer books (recap, no charge)
//   2. picked_up  → fired on booked → picked_up (no prices)
//   3. delivered  → fired on out_for_delivery → delivered (receipt + photo)
//
// Transport: Supabase Edge Function `send-order-email` calls the Resend
// API. RESEND_API_KEY lives in Supabase secrets — never touched here.
//
// The Edge Function accepts { kind, order_id } and re-reads every field
// server-side. The client sends nothing but the id + kind.

import { supabase } from './supabase'
import { showToast } from '../components/Toast'

const LABEL = {
  booked: 'Confirmation email',
  picked_up: 'Picked-up email',
  delivered: 'Delivered email',
}

// `silent` = the CUSTOMER triggered this. Staff toasts are a work tool;
// a customer booking a pickup must never see "Confirmation email sent",
// and must certainly never see it fail. Errors still reach the console.
async function invoke(kind, orderId, { silent = false } = {}) {
  if (!orderId) {
    // eslint-disable-next-line no-console
    console.info(`[email:${kind}] skipped — no order_id`)
    return null
  }
  const label = LABEL[kind] || 'Email'
  const toast = (msg) => { if (!silent) showToast(msg) }
  try {
    const { data, error } = await supabase.functions.invoke('send-order-email', {
      body: { kind, order_id: orderId },
    })
    if (error) {
      // eslint-disable-next-line no-console
      console.error(`[email:${kind}] invoke failed`, error, data)
      const detail = (data && (data.error || data.message)) || error.message || 'unknown'
      toast(`${label} not sent — ${detail}`)
      return null
    }
    if (data?.ok === false || data?.error) {
      toast(`${label} not sent — ${data.error || 'rejected'}`)
      return data
    }
    toast(`${label} sent`)
    return data
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[email:${kind}] request failed`, err)
    toast(`${label} failed — ${err?.message || 'network'}`)
    return null
  }
}

// Fired by the customer's own browser at booking. Silent by design.
export function sendBooked(orderId) { return invoke('booked', orderId, { silent: true }) }
export function sendPickedUp(orderId) { return invoke('picked_up', orderId) }
export function sendDelivered(orderId) { return invoke('delivered', orderId) }
